import { defineStore, acceptHMRUpdate } from 'pinia';
import isEmpty from 'lodash/isEmpty';
import { createFrozenMap } from '@/vuex/utils';
import { fetchMetaGroups, fetchMetaGroup, createMetaGroup as createMetaGroupApi, addCategoryToMetaGroup as addCategoryToMetaGroupApi, removeCategoryFromMetaGroup as removeCategoryFromMetaGroupApi } from '@/stores/api/meta-groups.api';
import { localizeMetaGroups } from '@/stores/helpers/meta-groups.helper';

export interface MetaGroup {
    id: string;
    [key: string]: unknown;
}

interface MetaGroupsState {
    map: Record<string, MetaGroup>;
    fetching: boolean;
    error: unknown;
}

export function getInitialState (): MetaGroupsState {
    return {
        map: {},
        fetching: false,
        error: undefined,
    };
}

export const useMetaGroupsStore = defineStore('metaGroups', {
    state: getInitialState,
    actions: {
        setMap ({ map }: { map: Record<string, MetaGroup> }) {
            this.map = map;
        },
        setError ({ error }: { error: unknown }) {
            this.error = error;
        },
        setFetching ({ fetching }: { fetching: boolean }) {
            this.fetching = fetching;
        },
        setMapAtKey ({ metaGroup }: { metaGroup: MetaGroup }) {
            this.map[metaGroup.id] = metaGroup;
        },
        async loadAll ({ reFetch = false, items = true }: { reFetch?: boolean; items?: boolean } = {}) {
            if (!isEmpty(this.map) && !reFetch) {
                return;
            }

            this.setFetching({ fetching: true });

            try {
                const { data: metaGroups } = await fetchMetaGroups({ items });
                const localizedMetaGroups = localizeMetaGroups(metaGroups);
                this.setFetching({ fetching: false });
                this.setMap({
                    map: createFrozenMap(localizedMetaGroups) as Record<string, MetaGroup>,
                });
            } catch (error) {
                this.setFetching({ fetching: false });
                this.setError({ error });
                throw error;
            }
        },
        async loadOne ({ id, reFetch = false, items = true }: { id: string; reFetch?: boolean; items?: boolean } = {} as never) {
            if (!reFetch && this.map[id]) {
                return this.map[id];
            }

            try {
                const { data: metaGroup } = await fetchMetaGroup({ id, items });
                const [localized] = localizeMetaGroups([metaGroup]);
                this.setMapAtKey({
                    metaGroup: Object.freeze(localized) as MetaGroup,
                });

                return localized;
            } catch (error) {
                this.setError({ error });
                throw error;
            }
        },
        async createMetaGroup (payload: Record<string, unknown> = {}) {
            try {
                const { data } = await createMetaGroupApi(payload);
                const metaGroup = Array.isArray(data)
                    ? data.find((entity) => entity.type === 'metaGroup')
                    : data;

                this.setMapAtKey({
                    metaGroup: Object.freeze(metaGroup) as MetaGroup,
                });

                return metaGroup;
            } catch (error) {
                this.setError({ error });
                throw error;
            }
        },
        async addCategoryToMetaGroup ({ metaGroupId, categoryId }: { metaGroupId: string; categoryId: string }) {
            const response = await addCategoryToMetaGroupApi({ metaGroupId, categoryId });
            await this.loadOne({ id: metaGroupId, reFetch: true, items: true })
                .catch((error) => console.error('Failed to refresh metagroup after add:', error));

            return response;
        },
        async removeCategoryFromMetaGroup ({ metaGroupId, categoryId }: { metaGroupId: string; categoryId: string }) {
            const response = await removeCategoryFromMetaGroupApi({ metaGroupId, categoryId });
            await this.loadOne({ id: metaGroupId, reFetch: true, items: true })
                .catch((error) => console.error('Failed to refresh metagroup after remove:', error));

            return response;
        },
    },
    getters: {
        list: (state) => Object.values(state.map),
    },
});

if (module.hot) {
    acceptHMRUpdate(useMetaGroupsStore, module.hot);
}
